import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut, createUserWithEmailAndPassword, updateProfile as updateFirebaseProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileSyncBlocked, setProfileSyncBlocked] = useState(false);
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';

  const recordAttendanceIfStudent = useCallback(async (mappedUser) => {
    if (!useFirebase || !mappedUser?.id) return;
    const roles = mappedUser.roles || [mappedUser.role];
    if (!roles.includes('student')) return;
    try {
      const todayKey = new Date().toLocaleDateString('en-CA');
      const attendanceId = `${mappedUser.id}_${todayKey}`;
      const attendanceRef = doc(db, 'attendanceRecords', attendanceId);
      await setDoc(
        attendanceRef,
        {
          userId: mappedUser.id,
          date: todayKey,
          name: `${mappedUser.firstName || ''} ${mappedUser.lastName || ''}`.trim() || 'Student',
          email: mappedUser.email || '',
          createdAt: serverTimestamp()
        },
        { merge: true }
      );
    } catch (error) {
      if (error?.code !== 'permission-denied') {
        console.error('Failed to record attendance', error);
      }
    }
  }, [useFirebase]);

  useEffect(() => {
    if (useFirebase) {
      const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
        if (!fbUser) {
          setUser(null);
          setLoading(false);
          return;
        }
        const syncProfile = async () => {
          const fallbackName = fbUser.displayName ? fbUser.displayName.split(' ') : ['User', ''];
          const userDocRef = doc(db, 'users', fbUser.uid);
          try {
            if (profileSyncBlocked) {
              setUser({
                id: fbUser.uid,
                email: fbUser.email,
                firstName: fallbackName[0],
                lastName: fallbackName[1] || '',
                phone: '',
                role: 'student',
                roles: ['student'],
                activeRole: 'student',
                lmsActive: false,
                avatar: (fallbackName[0]?.[0] || 'U') + (fallbackName[1]?.[0] || '')
              });
              setLoading(false);
              return;
            }
            await fbUser.getIdToken(true);
            const userSnap = await getDoc(userDocRef);
            if (userSnap.exists()) {
              const profile = userSnap.data();
              const mappedUser = {
                id: fbUser.uid,
                email: fbUser.email,
                firstName: profile.firstName || fallbackName[0],
                lastName: profile.lastName || fallbackName[1] || '',
                phone: profile.phone || '',
                role: profile.role || 'student',
                roles: profile.roles,
                activeRole: profile.activeRole || profile.role || 'student',
                lmsActive: profile.lmsActive || false,
                intake: profile.intake,
                courseId: profile.courseId,
                pendingApproval: profile.pendingApproval || false,
                pendingCourseId: profile.pendingCourseId,
                pendingIntake: profile.pendingIntake,
                leadId: profile.leadId,
                accessStatus: profile.accessStatus,
                paymentPlan: profile.paymentPlan,
                trialStartedAt: profile.trialStartedAt,
                trialEndsAt: profile.trialEndsAt,
                paymentDue: profile.paymentDue,
                avatar: profile.avatar || (fallbackName[0]?.[0] || 'U') + (fallbackName[1]?.[0] || '')
              };
              setUser(mappedUser);
              recordAttendanceIfStudent(mappedUser);
              setLoading(false);
              return;
            }

            const profile = {
              firstName: fallbackName[0],
              lastName: fallbackName[1] || '',
              phone: '',
              role: 'student',
              roles: ['student'],
              activeRole: 'student',
              lmsActive: false,
              avatar: (fallbackName[0]?.[0] || 'U') + (fallbackName[1]?.[0] || ''),
              createdAt: serverTimestamp()
            };
            await setDoc(userDocRef, profile, { merge: true });
            const createdUser = { id: fbUser.uid, email: fbUser.email, ...profile };
            setUser(createdUser);
            recordAttendanceIfStudent(createdUser);
            setLoading(false);
          } catch (error) {
            if (error?.code === 'permission-denied') {
              setProfileSyncBlocked(true);
            }
            const minimalUser = {
              id: fbUser.uid,
              email: fbUser.email,
              firstName: fallbackName[0],
              lastName: fallbackName[1] || '',
              phone: '',
              role: 'student',
              roles: ['student'],
              activeRole: 'student',
              lmsActive: false,
              pendingApproval: false,
              avatar: (fallbackName[0]?.[0] || 'U') + (fallbackName[1]?.[0] || '')
            };
            setUser(minimalUser);
            recordAttendanceIfStudent(minimalUser);
            setLoading(false);
            if (error?.code !== 'permission-denied') {
              // keep app usable even if profile sync fails
              console.error('Failed to sync profile', error);
            }
          }
        };

        syncProfile();
      });
      return () => unsubscribe();
    }

    setUser(null);
    setLoading(false);
  }, [useFirebase, profileSyncBlocked, recordAttendanceIfStudent]);

  const login = (email, password) => {
    if (useFirebase) {
      return signInWithEmailAndPassword(auth, email, password).then(async (credential) => {
        const fbUser = credential.user;
        const fallbackName = fbUser.displayName ? fbUser.displayName.split(' ') : ['User', ''];
        let profile = null;
        try {
          if (!profileSyncBlocked) {
            await fbUser.getIdToken(true);
            const userDocRef = doc(db, 'users', fbUser.uid);
            const userSnap = await getDoc(userDocRef);
            profile = userSnap.exists() ? userSnap.data() : null;
          }
        } catch (error) {
          if (error?.code === 'permission-denied') {
            setProfileSyncBlocked(true);
          }
        }
        const mappedUser = {
          id: fbUser.uid,
          email: fbUser.email,
          firstName: profile?.firstName || fallbackName[0],
          lastName: profile?.lastName || fallbackName[1] || '',
          phone: profile?.phone || '',
          role: profile?.role || 'student',
          roles: profile?.roles || (profile?.role ? [profile.role] : ['student']),
          activeRole: profile?.activeRole || profile?.role || 'student',
          lmsActive: profile?.lmsActive || false,
          intake: profile?.intake,
          courseId: profile?.courseId,
          pendingApproval: profile?.pendingApproval || false,
          pendingCourseId: profile?.pendingCourseId,
          pendingIntake: profile?.pendingIntake,
          leadId: profile?.leadId,
          accessStatus: profile?.accessStatus,
          paymentPlan: profile?.paymentPlan,
          trialStartedAt: profile?.trialStartedAt,
          trialEndsAt: profile?.trialEndsAt,
          paymentDue: profile?.paymentDue,
          avatar: profile?.avatar || (fallbackName[0]?.[0] || 'U') + (fallbackName[1]?.[0] || '')
        };
        setUser(mappedUser);
        recordAttendanceIfStudent(mappedUser);
        return mappedUser;
      });
    }

    return Promise.reject(new Error('Firebase authentication is required.'));
  };

  const setSessionUser = (newUser) => {
    if (!newUser) return null;
    const { password: _, ...userWithoutPassword } = newUser;
    const activeRole = userWithoutPassword.activeRole || userWithoutPassword.role;
    const userWithRole = { ...userWithoutPassword, activeRole };
    setUser(userWithRole);
    return userWithRole;
  };

  const logout = () => {
    if (useFirebase) {
      signOut(auth);
      setUser(null);
      return;
    }
    setUser(null);
  };

  const updateLocalProfile = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    return updatedUser;
  };

  const setActiveRole = (nextRole) => {
    if (!user) return null;
    const updatedUser = { ...user, activeRole: nextRole };
    if (useFirebase) {
      const userRef = doc(db, 'users', user.id);
      setDoc(userRef, { activeRole: nextRole }, { merge: true }).catch((error) => {
        if (error?.code !== 'permission-denied') {
          console.error('Failed to persist active role', error);
        }
      });
      setUser(updatedUser);
      return updatedUser;
    }
    setUser(updatedUser);
    return updatedUser;
  };

  const register = async ({ email, password, firstName, lastName, role = 'student', profile: extraProfile = {} }) => {
    if (!useFirebase) {
      throw new Error('Firebase is required for registration.');
    }
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await updateFirebaseProfile(credential.user, { displayName: `${firstName} ${lastName}` });
    const profile = {
      firstName,
      lastName,
      phone: extraProfile.phone || '',
      role,
      roles: [role],
      activeRole: role,
      lmsActive: false,
      avatar: `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase(),
      ...extraProfile,
      createdAt: serverTimestamp()
    };
    await setDoc(doc(db, 'users', credential.user.uid), profile, { merge: true });
    const mappedUser = { id: credential.user.uid, email, ...profile };
    setUser(mappedUser);
    return mappedUser;
  };

  const value = {
    user,
    loading,
    login,
    setSessionUser,
    setActiveRole,
    register,
    logout,
    updateProfile: updateLocalProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
